'use client';

import React, { CSSProperties, useEffect, useState } from 'react';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';

type Props = {
  progress: number;
  progress2?: number;
  height?: number;
  barColor?: string;
  barClassName?: string;
  progressBarClassName?: string;
  progressBar2ClassName?: string;
  barColor2?: string;
  containerColor?: string;
  tooltipMessage?: React.ReactNode;
  sx?: CSSProperties;
  progressTransition?: { duration?: number };
  showValue?: boolean;
};

/**
 * A component which can work as a single or double progress bar.
 * @param   {Props} props Component props.
 * @param   {number} props.progress Percentage of progress bar which makes up the primary bar.
 * @param   {number} [props.progress2] Percentage of progress bar which makes up the second bar.
 * @param   {number} [props.height=10] Height of the progress bar.
 * @param   {string} [props.barColor='primary'] Primary bar color.
 * @param   {string} [props.barColor2='red'] Second bar color.
 * @param   {string} [props.containerColor='#e5e7eb'] Background color.
 * @param   {React.ReactNode} [props.tooltipMessage] A component which will be displayed as a tooltip on hover of progress bar.
 * @returns  {React.ReactNode} A single or double progress bar.
 */
const ProgressBar = (props: Props): JSX.Element => {
  const {
    progress,
    progress2 = 0,
    height = 10,
    containerColor = '#e5e7eb',
    barClassName = '',
    progressBarClassName = '',
    progressBar2ClassName = '',
    barColor = 'primary',
    barColor2 = 'red',
    tooltipMessage = '',
    sx,
    progressTransition = { duration: 0.3 },
    showValue,
  } = props;

  const [progressWidth, setProgressWidth] = useState(0);
  const [progress2Width, setProgress2Width] = useState(0);

  const colors = useColors();

  useEffect(() => {
    setProgressWidth(progress * 100);
    setProgress2Width(progress2 * 100);
  }, [progress, progress2]);

  const progressValue = `${String(Math.ceil(progress * 100))}%`;

  return (
    <Flex className={`items-center w-full`}>
      <Box
        className={`w-full rounded-[100px] inline-flex ${barClassName}`}
        style={{
          height,
          backgroundColor: containerColor,
          ...sx,
        }}
        data-tip
        data-tooltip-id="progressBar-tooltip"
        aria-hidden
      >
        <Box
          style={{
            backgroundColor: colors[barColor] || barColor,
            borderRadius: progress2 ? '100px 0 0 100px' : 100,
            transitionDuration: `${progressTransition.duration}s`,
            width: `${progressWidth}%`,
          }}
          className={`h-full max-w-full transition-width ${
            progressBarClassName || ''
          } `}
        />

        {progress2 ? (
          <Box
            style={{
              backgroundColor: colors[barColor2] || barColor2,
              borderRadius: progress ? '0 100px 100px 0' : 100,
              transitionDuration: `${progressTransition.duration}s`,
              width: `${progress2Width}%`,
            }}
            className={`h-full max-w-full transition-width ${
              progressBar2ClassName || ''
            } `}
          />
        ) : null}
      </Box>
      {tooltipMessage && (
        <Tooltip id="progressBar-tooltip" place="bottom">
          {tooltipMessage}
        </Tooltip>
      )}
      {showValue ? (
        <Flex className={`ml-1 text-sm`}>
          <span>{progressValue}</span>
        </Flex>
      ) : null}
    </Flex>
  );
};

export default ProgressBar;
